/**
 * Shared editor component for people-archive blocks (Agents, Agency).
 *
 * Both blocks expose the same control surface — display header, search, view
 * controls, columns, per-page and ordering — over a live server-rendered
 * preview. Only the block name and order-by options differ.
 *
 * @package
 * @since   3.5.0
 */

import { __ } from '@wordpress/i18n';
import { useBlockProps, InspectorControls } from '@wordpress/block-editor';
import {
	PanelBody,
	SelectControl,
	RangeControl,
	ToggleControl,
	TextControl,
} from '@wordpress/components';
import BlockPreview from './BlockPreview';

const VIEW_OPTIONS = [
	{ label: __( 'Grid', 'havenlytics' ), value: 'grid' },
	{ label: __( 'List', 'havenlytics' ), value: 'list' },
];

const ORDER_OPTIONS = [
	{ label: __( 'Ascending', 'havenlytics' ), value: 'ASC' },
	{ label: __( 'Descending', 'havenlytics' ), value: 'DESC' },
];

/**
 * Build a people-archive edit component bound to a specific block.
 *
 * @param {string} blockName      Registered block name (e.g. havenlytics/agents).
 * @param {Array}  orderbyOptions SelectControl options for the order-by field.
 * @return {Function} A Gutenberg edit component.
 */
export default function createPeopleArchiveEdit( blockName, orderbyOptions ) {
	return function Edit( { attributes, setAttributes } ) {
		const {
			defaultView,
			columns,
			postsPerPage,
			orderby,
			order,
			showHeader,
			title,
			subtitle,
			showSearch,
			showViewControls,
		} = attributes;

		const blockProps = useBlockProps();

		return (
			<>
				<InspectorControls>
					<PanelBody
						title={ __( 'Layout', 'havenlytics' ) }
						initialOpen={ true }
					>
						<p className="components-base-control__help">
							{ __(
								'Controls the archive layout visitors see first.',
								'havenlytics'
							) }
						</p>
						<SelectControl
							label={ __( 'Default view', 'havenlytics' ) }
							value={ defaultView }
							options={ VIEW_OPTIONS }
							onChange={ ( value ) =>
								setAttributes( { defaultView: value } )
							}
							__nextHasNoMarginBottom
						/>
						<RangeControl
							label={ __( 'Columns', 'havenlytics' ) }
							value={ columns }
							min={ 1 }
							max={ 4 }
							onChange={ ( value ) =>
								setAttributes( { columns: value } )
							}
							help={ __( 'Used in grid view.', 'havenlytics' ) }
							__nextHasNoMarginBottom
						/>
						<RangeControl
							label={ __( 'Items per page', 'havenlytics' ) }
							value={ postsPerPage }
							min={ 1 }
							max={ 48 }
							onChange={ ( value ) =>
								setAttributes( { postsPerPage: value } )
							}
							__nextHasNoMarginBottom
						/>
					</PanelBody>

					<PanelBody
						title={ __( 'Header', 'havenlytics' ) }
						initialOpen={ false }
					>
						<ToggleControl
							label={ __( 'Show header', 'havenlytics' ) }
							checked={ !! showHeader }
							onChange={ ( value ) =>
								setAttributes( { showHeader: value } )
							}
							__nextHasNoMarginBottom
						/>
						{ showHeader && (
							<>
								<TextControl
									label={ __( 'Title', 'havenlytics' ) }
									value={ title }
									onChange={ ( value ) =>
										setAttributes( { title: value } )
									}
									__nextHasNoMarginBottom
								/>
								<TextControl
									label={ __( 'Subtitle', 'havenlytics' ) }
									value={ subtitle }
									onChange={ ( value ) =>
										setAttributes( { subtitle: value } )
									}
									__nextHasNoMarginBottom
								/>
							</>
						) }
					</PanelBody>

					<PanelBody
						title={ __( 'Query', 'havenlytics' ) }
						initialOpen={ false }
					>
						<SelectControl
							label={ __( 'Order by', 'havenlytics' ) }
							value={ orderby }
							options={ orderbyOptions }
							onChange={ ( value ) =>
								setAttributes( { orderby: value } )
							}
							__nextHasNoMarginBottom
						/>
						<SelectControl
							label={ __( 'Order', 'havenlytics' ) }
							value={ order }
							options={ ORDER_OPTIONS }
							onChange={ ( value ) =>
								setAttributes( { order: value } )
							}
							__nextHasNoMarginBottom
						/>
						<ToggleControl
							label={ __( 'Show search', 'havenlytics' ) }
							checked={ !! showSearch }
							onChange={ ( value ) =>
								setAttributes( { showSearch: value } )
							}
							__nextHasNoMarginBottom
						/>
						<ToggleControl
							label={ __( 'Show view controls', 'havenlytics' ) }
							checked={ !! showViewControls }
							onChange={ ( value ) =>
								setAttributes( { showViewControls: value } )
							}
							__nextHasNoMarginBottom
						/>
					</PanelBody>
				</InspectorControls>

				<div { ...blockProps }>
					<BlockPreview
						block={ blockName }
						attributes={ attributes }
					/>
				</div>
			</>
		);
	};
}
